import {
  createCameraMatrix,
  essentialToFundamental,
  filterTriangulatedByReprojection,
  fundamentalToEssential,
  poseToFundamental,
  recoverCameraPose,
  sampsonError,
  triangulateCorrespondences,
} from "./geometry";

function summarizeErrors(errors) {
  const sorted = errors.filter(Number.isFinite).sort((a, b) => a - b);

  if (sorted.length === 0) {
    return {
      min: null,
      median: null,
      mean: null,
      max: null,
    };
  }

  return {
    min: sorted[0],
    median: sorted[Math.floor(sorted.length / 2)],
    mean: sorted.reduce((sum, error) => sum + error, 0) / sorted.length,
    max: sorted[sorted.length - 1],
  };
}

// Sparse reprojection error using the recovered camera pose.
export function evaluateSparseReprojection({ geometry, reconstruction }) {
  if (!reconstruction || !geometry || geometry.inliers.length === 0) {
    return null;
  }

  const triangulated = triangulateCorrespondences(
    geometry.inliers,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
    reconstruction.R,
    reconstruction.t,
  );

  const filtered = filterTriangulatedByReprojection(
    triangulated.accepted,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
    reconstruction.R,
    reconstruction.t,
    Infinity,
  );

  const errors = filtered.accepted.map((point) => point.reprojectionError);

  return {
    result: filtered,
    metrics: {
      input: geometry.inliers.length,
      positiveDepth: triangulated.accepted.length,
      ...summarizeErrors(errors),
    },
  };
}

// Convert recovered R/t back to F and evaluate the RANSAC inliers.
export function evaluatePoseFundamental({ geometry, reconstruction }) {
  if (!geometry || !reconstruction || geometry.inliers.length === 0) {
    return null;
  }

  const fundamentalMatrix = poseToFundamental(
    reconstruction.R,
    reconstruction.t,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
  );

  const errors = geometry.inliers.map((correspondence) =>
    sampsonError(fundamentalMatrix, correspondence),
  );

  return {
    result: fundamentalMatrix,
    metrics: {
      input: geometry.inliers.length,
      ...summarizeErrors(errors),
    },
  };
}

// Constrained E -> F round-trip against the original RANSAC inliers.
export function evaluateEssentialRoundTrip({ geometry, reconstruction }) {
  if (
    !geometry ||
    !reconstruction ||
    !reconstruction.essentialMatrix ||
    geometry.inliers.length === 0
  ) {
    return null;
  }

  const fundamentalMatrix = essentialToFundamental(
    reconstruction.essentialMatrix,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
  );

  const errors = geometry.inliers.map((correspondence) =>
    sampsonError(fundamentalMatrix, correspondence),
  );

  return {
    result: fundamentalMatrix,
    metrics: {
      input: geometry.inliers.length,
      ...summarizeErrors(errors),
    },
  };
}

// Raw F -> E -> F without enforcing essential-matrix singular values.
export function evaluateRawEssentialRoundTrip({ geometry, reconstruction }) {
  if (!geometry || !reconstruction || geometry.inliers.length === 0) {
    return null;
  }

  const rawEssentialMatrix = fundamentalToEssential(
    geometry.F,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
    false,
  );

  const fundamentalMatrix = essentialToFundamental(
    rawEssentialMatrix,
    reconstruction.cameraMatrixA,
    reconstruction.cameraMatrixB,
  );

  const errors = geometry.inliers.map((correspondence) =>
    sampsonError(fundamentalMatrix, correspondence),
  );

  return {
    result: fundamentalMatrix,
    rawEssentialMatrix,
    metrics: {
      input: geometry.inliers.length,
      ...summarizeErrors(errors),
    },
  };
}

// Recover pose from raw E using default camera matrices and measure reprojection.
export function evaluateRawEssentialReprojection({
  geometry,
  featuresA,
  featuresB,
}) {
  if (!geometry || !featuresA || !featuresB || geometry.inliers.length < 8) {
    return null;
  }

  const cameraMatrixA = createCameraMatrix(featuresA.width, featuresA.height);
  const cameraMatrixB = createCameraMatrix(featuresB.width, featuresB.height);

  const rawEssentialMatrix = fundamentalToEssential(
    geometry.F,
    cameraMatrixA,
    cameraMatrixB,
    false,
  );

  const pose = recoverCameraPose(
    rawEssentialMatrix,
    geometry.inliers,
    cameraMatrixA,
    cameraMatrixB,
  );

  const triangulated = triangulateCorrespondences(
    geometry.inliers,
    cameraMatrixA,
    cameraMatrixB,
    pose.R,
    pose.t,
  );

  const reprojected = filterTriangulatedByReprojection(
    triangulated.accepted,
    cameraMatrixA,
    cameraMatrixB,
    pose.R,
    pose.t,
    Infinity,
  );

  const errors = reprojected.accepted.map((point) => point.reprojectionError);

  return {
    result: reprojected,
    rawEssentialMatrix,
    pose,
    metrics: {
      input: geometry.inliers.length,
      positiveDepth: triangulated.accepted.length,
      ...summarizeErrors(errors),
    },
  };
}
